// Shopify Storefront API client (2025-07).
// All product/collection queries use @inContext(country:) so prices come
// back in the correct market currency (Shopify Markets).

import { toast } from "@/lib/toast";

export const SHOPIFY_API_VERSION = "2025-07";
export const SHOPIFY_STORE_PERMANENT_DOMAIN = "mees38-fy.myshopify.com";
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
export const SHOPIFY_STOREFRONT_TOKEN = "29ad25c6250f624153cdb25299b063b9";

// Storefront accepts any ISO 3166-1 alpha-2 code (CountryCode enum on the API).
// We type as string to support all markets defined in src/lib/markets.ts.
export type CountryCode = string;

/**
 * Shopify CDN image transformer.
 * Adds `?width=&height=&crop=center` params so Shopify serves a properly-sized
 * image instead of the original (often 2000+px) file. Hugely reduces bandwidth
 * and rendering cost on mobile.
 *
 * Usage:
 *   <img src={shopifyImg(url, 600, 800)} />
 *   <img src={shopifyImg(url, 400)} srcSet={shopifySrcSet(url, [400, 800])} />
 */
export function shopifyImg(url: string, width: number, height?: number): string {
  if (!url) return url;
  // Already transformed — leave as-is to avoid double params
  if (url.includes("width=") || url.includes("?width") || url.includes("&width")) return url;
  const sep = url.includes("?") ? "&" : "?";
  const params = new URLSearchParams();
  params.set("width", String(width));
  if (height) params.set("height", String(height));
  params.set("crop", "center");
  return `${url}${sep}${params.toString()}`;
}

/**
 * Build a srcset string for responsive images.
 * Browsers pick the best size based on viewport + DPR automatically.
 */
export function shopifySrcSet(url: string, widths: number[], aspectRatio?: number): string {
  if (!url) return "";
  return widths
    .map((w) => {
      const h = aspectRatio ? Math.round(w * aspectRatio) : undefined;
      return `${shopifyImg(url, w, h)} ${w}w`;
    })
    .join(", ");
}

export interface ShopifyMoney {
  amount: string;
  currencyCode: string;
}

export interface ShopifyImage {
  url: string;
  altText: string | null;
  width?: number;
  height?: number;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: ShopifyMoney;
  availableForSale: boolean;
  selectedOptions: Array<{ name: string; value: string }>;
}

export interface ShopifyMediaVideo {
  mediaContentType: "VIDEO";
  alt: string | null;
  sources: Array<{ url: string; mimeType: string; format: string }>;
  previewImage?: { url: string } | null;
}

export interface ShopifyMediaImage {
  mediaContentType: "IMAGE";
  image: ShopifyImage;
  alt: string | null;
}

/**
 * موديل ٣D من Shopify (Model3d) — يُرفَع كـ"3D model" في Shopify Admin → Products → Media.
 * sources تحتوي عادةً:
 *   - `glb`  (mimeType: "model/gltf-binary")     ← أندرويد + ديسكتوب
 *   - `usdz` (mimeType: "model/vnd.usdz+zip")    ← iOS AR Quick Look
 */
export interface ShopifyMediaModel3d {
  mediaContentType: "MODEL_3D";
  alt: string | null;
  sources: Array<{ url: string; mimeType: string; format: string }>;
  previewImage?: { url: string } | null;
}

export type ShopifyMedia =
  | ShopifyMediaImage
  | ShopifyMediaVideo
  | ShopifyMediaModel3d
  | { mediaContentType: string };

export interface ShopifyProductNode {
  id: string;
  title: string;
  description: string;
  descriptionHtml?: string;
  handle: string;
  vendor?: string;
  productType?: string;
  tags?: string[];
  priceRange: { minVariantPrice: ShopifyMoney };
  images: { edges: Array<{ node: ShopifyImage }> };
  media?: { edges: Array<{ node: ShopifyMedia }> };
  variants: { edges: Array<{ node: ShopifyVariant }> };
  options: Array<{ name: string; values: string[] }>;
  /**
   * Metafields متعدّدة اللغات للوصف وتفاصيل الـnotes.
   * يكتبها الأدمن في Shopify Admin → Metafields بـnamespace=`descriptions`
   * keys: `ar` (عربي), `en` (إنجليزي), `notes_top_ar/en`, `notes_heart_ar/en`, `notes_base_ar/en`, `editor_note_ar/en`
   * إن غابت → fallback للوصف الافتراضيّ من Shopify ثمّ نصّ i18n عامّ.
   */
  descriptionAr?: { value: string } | null;
  descriptionEn?: { value: string } | null;
  notesTopAr?: { value: string } | null;
  notesTopEn?: { value: string } | null;
  notesHeartAr?: { value: string } | null;
  notesHeartEn?: { value: string } | null;
  notesBaseAr?: { value: string } | null;
  notesBaseEn?: { value: string } | null;
  editorNoteAr?: { value: string } | null;
  editorNoteEn?: { value: string } | null;
  titleAr?: { value: string } | null;
  titleEn?: { value: string } | null;
  /** Collections this product belongs to (used to highlight the active tab in CollectionTabsNav). */
  collections?: { edges: Array<{ node: { handle: string; title: string } }> };
}

export interface ShopifyProduct {
  node: ShopifyProductNode;
}

/**
 * Tags التي تُخفي المنتج من واجهة الشراء العامّة (المتجر/المجموعات/البحث/التوصيات).
 * المنتج يبقى متاحاً في Shopify ويمكن إضافته كهديّة/تستر عبر `shopify_variant_id` مباشرةً.
 *
 * كيف يستخدمها صاحب المتجر:
 *   في Shopify Admin → Products → افتح منتج التستر → Tags → أضف واحداً من:
 *     `tester` · `gift-only` · `sample` · `internal` · `internal-gift` · `hidden`
 *   احفظ. يختفي فوراً من /shop و /collection/* والبحث وتوصيات السلة،
 *   لكن يبقى قابلاً للإضافة كهديّة عبر نظام `gift_program_samples`.
 */
const HIDDEN_PRODUCT_TAGS = new Set([
  "tester",
  "gift-only",
  "sample",
  "internal",
  "internal-gift",
  "internal-only",
  "discovery-component",
  "hidden",
]);

/** يعيد true إذا كان المنتج يجب إخفاؤه من واجهة الشراء العامّة. */
export function isHiddenFromStorefront(p: ShopifyProduct): boolean {
  const tags = (p.node.tags ?? []).map((x) => x.toLowerCase().trim());
  if (tags.some((t) => HIDDEN_PRODUCT_TAGS.has(t))) return true;
  const title = (p.node.title ?? "").toLowerCase();
  if (title.startsWith("sample ") || title.startsWith("sample-") || title.startsWith("tester ")) {
    return true;
  }
  return false;
}

/** يفلتر مصفوفة منتجات من تلك المخفيّة. */
export function filterVisibleProducts(products: ShopifyProduct[]): ShopifyProduct[] {
  return products.filter((p) => !isHiddenFromStorefront(p));
}

// ─────────────────────────────────────────────────────────────────────
// In-memory cache for Shopify Storefront API responses.
// On Cloudflare Workers each isolate keeps its own Map — effectively
// a per-edge-location, per-isolate cache.  TTL = 5 min by default.
// ─────────────────────────────────────────────────────────────────────
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const apiCache = new Map<string, { data: unknown; ts: number }>();

function cacheKey(query: string, variables: Record<string, unknown>): string {
  return query + "\n" + JSON.stringify(variables);
}

export interface StorefrontRequestOptions {
  /** Set to 0 to bypass cache entirely (e.g. for mutations). Default: 5 min */
  cacheTtlMs?: number;
}

export async function storefrontApiRequest<T = any>(
  query: string,
  variables: Record<string, unknown> = {},
  opts?: StorefrontRequestOptions,
): Promise<{ data?: T; errors?: Array<{ message: string }> } | undefined> {
  const ttl = opts?.cacheTtlMs ?? CACHE_TTL_MS;

  // Check cache first
  if (ttl > 0) {
    const key = cacheKey(query, variables);
    const cached = apiCache.get(key);
    if (cached && Date.now() - cached.ts < ttl) {
      return cached.data as { data?: T; errors?: Array<{ message: string }> };
    }
  }

  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    if (typeof window !== "undefined") {
      toast.error("Shopify: Payment required", {
        description:
          "Shopify API access requires an active billing plan. Visit https://admin.shopify.com to upgrade.",
      });
    }
    return;
  }

  if (!response.ok) {
    throw new Error(`Shopify HTTP error: ${response.status}`);
  }

  const data = await response.json();
  if (data.errors) {
    throw new Error(`Shopify error: ${data.errors.map((e: any) => e.message).join(", ")}`);
  }

  // Store in cache
  if (ttl > 0) {
    const key = cacheKey(query, variables);
    apiCache.set(key, { data, ts: Date.now() });
    // Evict old entries if cache grows too large (> 200 entries)
    if (apiCache.size > 200) {
      const now = Date.now();
      for (const [k, v] of apiCache) {
        if (now - v.ts > ttl) apiCache.delete(k);
      }
    }
  }

  return data;
}

export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String, $country: CountryCode!)
  @inContext(country: $country) {
    products(first: $first, query: $query, sortKey: BEST_SELLING) {
      edges {
        node {
          id
          title
          description
          handle
          vendor
          productType
          tags
          priceRange { minVariantPrice { amount currencyCode } }
          images(first: 8) { edges { node { url altText width height } } }
          variants(first: 5) {
            edges {
              node {
                id
                title
                price { amount currencyCode }
                availableForSale
                selectedOptions { name value }
              }
            }
          }
          options { name values }
          collections(first: 20) { edges { node { handle title } } }
          descriptionAr: metafield(namespace: "descriptions", key: "ar") { value }
          descriptionEn: metafield(namespace: "descriptions", key: "en") { value }
          titleAr: metafield(namespace: "descriptions", key: "title_ar") { value }
          titleEn: metafield(namespace: "descriptions", key: "title_en") { value }
        }
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProduct($handle: String!, $country: CountryCode!)
  @inContext(country: $country) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      vendor
      productType
      tags
      priceRange { minVariantPrice { amount currencyCode } }
      images(first: 10) { edges { node { url altText width height } } }
      media(first: 10) {
        edges {
          node {
            mediaContentType
            ... on MediaImage { image { url altText width height } alt }
            ... on Video {
              alt
              sources { url mimeType format }
              previewImage { url }
            }
            ... on Model3d {
              alt
              sources { url mimeType format }
              previewImage { url }
            }
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            price { amount currencyCode }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
      options { name values }
      descriptionAr: metafield(namespace: "descriptions", key: "ar") { value }
      descriptionEn: metafield(namespace: "descriptions", key: "en") { value }
      notesTopAr: metafield(namespace: "descriptions", key: "notes_top_ar") { value }
      notesTopEn: metafield(namespace: "descriptions", key: "notes_top_en") { value }
      notesHeartAr: metafield(namespace: "descriptions", key: "notes_heart_ar") { value }
      notesHeartEn: metafield(namespace: "descriptions", key: "notes_heart_en") { value }
      notesBaseAr: metafield(namespace: "descriptions", key: "notes_base_ar") { value }
      notesBaseEn: metafield(namespace: "descriptions", key: "notes_base_en") { value }
      editorNoteAr: metafield(namespace: "descriptions", key: "editor_note_ar") { value }
      editorNoteEn: metafield(namespace: "descriptions", key: "editor_note_en") { value }
      titleAr: metafield(namespace: "descriptions", key: "title_ar") { value }
      titleEn: metafield(namespace: "descriptions", key: "title_en") { value }
      collections(first: 8) { edges { node { handle title } } }
    }
  }
`;

export async function fetchProducts(
  first = 24,
  country: CountryCode = "AE",
  query?: string,
): Promise<ShopifyProduct[]> {
  const res = await storefrontApiRequest<{ products: { edges: ShopifyProduct[] } }>(
    PRODUCTS_QUERY,
    { first, query, country },
  );
  const edges = res?.data?.products?.edges ?? [];
  // إخفاء منتجات التستر/العينات من الواجهة العامّة (تبقى متاحة بالـvariantId للهديّة).
  return filterVisibleProducts(edges);
}

export async function fetchProductByHandle(
  handle: string,
  country: CountryCode = "AE",
  options?: StorefrontRequestOptions,
): Promise<ShopifyProductNode | null> {
  const res = await storefrontApiRequest<{ product: ShopifyProductNode | null }>(
    PRODUCT_BY_HANDLE_QUERY,
    { handle, country },
    options,
  );
  return res?.data?.product ?? null;
}

// ─────────────────────────────────────────────────────────────────────
// Collections
// ─────────────────────────────────────────────────────────────────────

export interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
  description: string;
  image: ShopifyImage | null;
  /** Optional video URL set via metafield `custom.video_url` on the collection in Shopify Admin. */
  videoUrl: string | null;
  /** Optional sort order via metafield `custom.sort_order` (number_integer). Lower = earlier. */
  sortOrder: number | null;
}

export const COLLECTIONS_QUERY = `
  query GetCollections($first: Int!) {
    collections(first: $first, sortKey: TITLE) {
      edges {
        node {
          id
          title
          handle
          description
          image { url altText width height }
          videoUrl: metafield(namespace: "custom", key: "video_url") { value }
          sortOrder: metafield(namespace: "custom", key: "sort_order") { value }
        }
      }
    }
  }
`;

export const COLLECTION_BY_HANDLE_QUERY = `
  query GetCollection($handle: String!, $first: Int!, $country: CountryCode!)
  @inContext(country: $country) {
    collection(handle: $handle) {
      id
      title
      handle
      description
      image { url altText width height }
      videoUrl: metafield(namespace: "custom", key: "video_url") { value }
      products(first: $first, sortKey: MANUAL) {
        edges {
          node {
            id
            title
            description
            handle
            vendor
            productType
            tags
            priceRange { minVariantPrice { amount currencyCode } }
            images(first: 4) { edges { node { url altText width height } } }
            variants(first: 5) {
              edges {
                node {
                  id
                  title
                  price { amount currencyCode }
                  availableForSale
                  selectedOptions { name value }
                }
              }
            }
            options { name values }
            descriptionAr: metafield(namespace: "descriptions", key: "ar") { value }
            descriptionEn: metafield(namespace: "descriptions", key: "en") { value }
            titleAr: metafield(namespace: "descriptions", key: "title_ar") { value }
            titleEn: metafield(namespace: "descriptions", key: "title_en") { value }
          }
        }
      }
    }
  }
`;

export async function fetchCollections(first = 20): Promise<ShopifyCollection[]> {
  const res = await storefrontApiRequest<{
    collections: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          handle: string;
          description: string;
          image: ShopifyImage | null;
          videoUrl: { value: string } | null;
          sortOrder: { value: string } | null;
        };
      }>;
    };
  }>(COLLECTIONS_QUERY, { first });

  const edges = res?.data?.collections?.edges ?? [];
  const mapped: ShopifyCollection[] = edges.map((e) => ({
    id: e.node.id,
    title: e.node.title,
    handle: e.node.handle,
    description: e.node.description,
    image: e.node.image,
    videoUrl: e.node.videoUrl?.value ?? null,
    sortOrder: e.node.sortOrder?.value != null ? Number(e.node.sortOrder.value) : null,
  }));
  // Sort by custom.sort_order metafield (asc); collections without it fall to the end.
  mapped.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
  return mapped;
}

export async function fetchCollectionByHandle(
  handle: string,
  country: CountryCode = "AE",
  first = 50,
): Promise<{ collection: ShopifyCollection; products: ShopifyProduct[] } | null> {
  const res = await storefrontApiRequest<{
    collection: {
      id: string;
      title: string;
      handle: string;
      description: string;
      image: ShopifyImage | null;
      videoUrl: { value: string } | null;
      products: { edges: ShopifyProduct[] };
    } | null;
  }>(COLLECTION_BY_HANDLE_QUERY, { handle, first, country });

  const c = res?.data?.collection;
  if (!c) return null;

  let products = c.products.edges;

  // Shopify قد يعيد collection موجودة لكن connection المنتجات فارغة رغم أن
  // البحث `collection:<handle>` يعثر على المنتجات الصحيحة. نعالج هذا الخلل
  // بفallback يحافظ على الهيرو/الميتاداتا من المجموعة نفسها بدل إظهار صفحة فارغة.
  if (products.length === 0) {
    const fallbackRes = await storefrontApiRequest<{
      products: { edges: ShopifyProduct[] };
    }>(
      PRODUCTS_QUERY,
      {
        first,
        query: `collection:${handle}`,
        country,
      },
    );

    products = fallbackRes?.data?.products?.edges ?? [];
  }

  return {
    collection: {
      id: c.id,
      title: c.title,
      handle: c.handle,
      description: c.description,
      image: c.image,
      videoUrl: c.videoUrl?.value ?? null,
      sortOrder: null,
    },
    products: filterVisibleProducts(products),
  };
}

